import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header } from "@/components/Header";
import { ThermalOptions } from "@/components/ThermalOptions";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { useT, getLocale, suggestLocaleFromCountry } from "@/lib/i18n";
import { SECTORS, SECTOR_LOAD_FACTOR, APPLICATIONS, OWNERSHIPS, GRIDS } from "@/lib/wizardOptions";
import { APPLICATION_OTHER } from "@/lib/application";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { FieldHelp } from "@/components/FieldHelp";
import { HowItWorks } from "@/components/HowItWorks";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const PRIORITIES = [
  { id: "lowest_upfront", labelKey: "wiz.priority.lowest_upfront" },
  { id: "lowest_lifetime", labelKey: "wiz.priority.lowest_lifetime" },
  { id: "max_reliability", labelKey: "wiz.priority.max_reliability" },
  // Was "lowest_emissions". The engine scores "lowest_emission", and the
  // plural silently fell back to the lowest-LIFETIME plant.
  { id: "lowest_emission", labelKey: "wiz.priority.lowest_emission" },
];
// iter45aq — these three are LOAD-FACTOR PRESETS and are now named after the
// factor each sets. None of the ids the engine recognises (`24/7_flat`,
// `day_shift`, `two_shift`, `three_shift`) was ever among them, so sending
// one only ever recorded `load_pattern_status: ignored_unknown_id`. They set
// `load_factor`, which is all they have ever actually done; `load_pattern` is
// left unset, which makes `not_supplied` a state that fires on real traffic
// instead of one that merely exists. The ids below are NOT remapped, so every
// existing `loadpattern-*` selector keeps working.
const LOAD_PATTERNS = [
  { id: "24/7", labelKey: "wiz.lf.p80", subKey: "wiz.lf.p80.sub", lf: 0.8 },
  { id: "daytime", labelKey: "wiz.lf.p45", subKey: "wiz.lf.p45.sub", lf: 0.45 },
  { id: "variable", labelKey: "wiz.lf.p35", subKey: "wiz.lf.p35.sub", lf: 0.35 },
];
const STEPS_KEYS = ["wiz.step.1", "wiz.step.2", "wiz.step.3", "wiz.step.4", "wiz.step.5", "wiz.step.6", "wiz.step.7"];

export const Configurator = () => {
  const navigate = useNavigate();
  const t = useT();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState({});
  const [form, setForm] = useState({
    sector: "Industrial",
    application: "",
    application_other_text: "",
    country: "Nigeria",
    region: "",
    peak_kw: 500,
    day_kwh: 0,
    load_factor: 0.70,
    altitude_m: 200,
    ambient_c: 32,
    irradiance: 5.5,
    grid_status: "weak_grid",
    outage_hours: 4,
    ownership: "captive",
    priority: "lowest_lifetime",
    fuel_price: 1.20,
    project_name: "",
    // Optional thermal add-on (iter43). Populated by <ThermalOptions/> on the
    // last wizard step; omitted from the payload when the buyer leaves the
    // "Advanced: thermal & gas" section untouched → engine output stays
    // bit-identical to pre-thermal behaviour.
    thermal_selection: null,
    // iter45ao Prompt-H Step 2 — First-class gas availability. Defaults to
    // 'unknown' so a buyer who never touches the picker gets the visible
    // conditional-fuel banner instead of a silent assumption of gas.
    gas_availability: "unknown",
    // J3 (Prompt J) — First-class diesel availability. Same shape as
    // gas_availability so the engine's `_filter_by_diesel_availability`
    // (candidate_engine.py:1811) can screen candidates when diesel is
    // hard-restricted (e.g., licence-restricted sites) or entirely
    // unavailable. Defaults to "available" for back-compat with every
    // saved project that predates this axis.
    diesel_availability: "available",
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/config").then((r) => {
      if (!cancelled) setCountries(r.data.countries || {});
    }).catch((err) => console.warn("[config] countries fetch failed", err?.message));
    return () => { cancelled = true; };
  }, []);

  // iter45l — Hydrate the form from a saved project when the buyer clicks
  // "Re-edit configuration" on the My Projects page. Runs once on mount;
  // the sessionStorage key is cleared immediately so a subsequent visit
  // to /configurator starts clean. iter45m — Also remember the source
  // `project_id` in local state so we can pass it to Results → save
  // → backend where it's used to replace the original locked row.
  const [replaceProjectId, setReplaceProjectId] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("configurator_prefill");
      if (!raw) return;
      sessionStorage.removeItem("configurator_prefill");
      const { input, project_id } = JSON.parse(raw);
      if (input && typeof input === "object") {
        setForm((f) => ({ ...f, ...input }));
      }
      if (project_id) setReplaceProjectId(project_id);
    } catch (e) { console.warn("[configurator] prefill hydrate failed", e?.message); }
  }, []);
  // iter45ac — Resume from Results' "Back" arrow. `?resume=priority` means
  // the buyer is tweaking after a run — rehydrate form from sessionStorage
  // .last_run.input and jump straight to step 6 (Priority) so they can
  // adjust one input without restarting the wizard.
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("resume") !== "priority") return;
    try {
      const raw = sessionStorage.getItem("last_run");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.input && typeof parsed.input === "object") {
        setForm((f) => ({ ...f, ...parsed.input }));
        setStep(6);
      }
    } catch (e) { console.warn("[configurator] resume hydrate failed", e?.message); }
  }, [location.search]);
  // Auto-fill from country — only overwrite blanks so user edits are preserved.
  useEffect(() => {
    const cd = countries[form.country];
    if (!cd) return;
    suggestLocaleFromCountry(form.country);
    setForm((f) => ({
      ...f,
      irradiance: f.irradiance || cd.irradiance,
      fuel_price: f.fuel_price || cd.fuel_price,
      region: cd.region,
      grid_status: f.grid_status || cd.grid_default,
    }));
  }, [form.country, countries]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const countryName = (c) => t("country." + c, c);
  const countryList = getLocale() === "fr"
    ? Object.keys(countries).sort((a, b) => countryName(a).localeCompare(countryName(b), "fr"))
    : Object.keys(countries).sort();

  // Stable ref for the ThermalOptions callback. Without useCallback the
  // parent creates a new arrow every render, ThermalOptions' useEffect
  // (which depends on onChange) then fires on every render → setForm
  // schedules another render → infinite loop → React aborts with
  // "Maximum update depth exceeded" and the Generate button click gets
  // swallowed. Stabilising the callback breaks the cycle.
  const setThermalSelection = useCallback((sel) => {
    setForm((f) => (f.thermal_selection === sel ? f : { ...f, thermal_selection: sel }));
  }, []);

  // Served-energy ruling: peak + daily energy decide, the load factor is derived (read-only).
  const lfDerived = form.peak_kw > 0 && form.day_kwh > 0 ? form.day_kwh / (form.peak_kw * 24) : null;
  const loadErr = lfDerived == null ? null
    : lfDerived > 1 + 1e-9 ? "wiz.load.err_above" : lfDerived < 1 / 24 - 1e-9 ? "wiz.load.err_below" : null;
  const canNext = () => {
    if (step === 0) return !!form.sector && !!form.application &&
      (form.application !== APPLICATION_OTHER || !!(form.application_other_text || "").trim());
    if (step === 1) return !!form.country;
    if (step === 2) return (form.peak_kw > 0 || form.day_kwh > 0) && !loadErr;
    if (step === 3) return form.irradiance > 0;
    if (step === 4) return !!form.grid_status;
    if (step === 5) return !!form.ownership;
    if (step === 6) return !!form.priority;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/configurator/run", form);
      sessionStorage.setItem("last_run", JSON.stringify({ input: form, result: r.data, replace_project_id: replaceProjectId }));
      track("first_pass_run", { sector: form.sector, country: form.country, priority: form.priority });
      navigate(`/results?run=${r.data.run_id}`);
    } catch (e) {
      toast.error(t("wiz.toast.compute_failed"));
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const progress = ((step + 1) / STEPS_KEYS.length) * 100;

  return (
    <div className="App" style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <Header />
      <div className="max-w-[1120px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="overline mb-2">{t("wiz.overline")}</div>
            <h1 className="font-display text-3xl md:text-4xl">{t("wiz.title")}</h1>
          </div>
          <span className="pill" style={{ background: "var(--white)", color: "var(--muted)", border: "1px solid var(--hairline)" }}>
            {t("wiz.step")} {step + 1} {t("wiz.of")} {STEPS_KEYS.length} · {t(STEPS_KEYS[step])}
          </span>
        </div>

        <div className="w-full h-1 rounded" style={{ background: "var(--hairline)" }}>
          <div style={{ width: `${progress}%`, height: "100%", background: "var(--brand-green)", borderRadius: 4, transition: "width 0.3s ease" }} />
        </div>

        <HowItWorks gridStatus={form.grid_status} />

        <div className="card p-8 md:p-10 mt-6" data-testid={`wizard-step-${step}`}>
          {step === 0 && (
            <>
              <StepHeading title={t("wiz.step.1")} sub={t("wiz.sector.sub")} help="help.sector" helpTestid="help-sector" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {SECTORS.map((s) => (
                  <SelectTile key={s.value} label={t(s.key)} selected={form.sector === s.value} onClick={() => {
                    const lf = SECTOR_LOAD_FACTOR[s.value] ?? 0.55;
                    set("sector", s.value);
                    set("application", "");
                    set("application_other_text", "");
                    set("load_factor", lf);
                  }} testid={`sector-${s.value.toLowerCase()}`} />
                ))}
              </div>
              <div className="mt-8">
                <div className="flex items-center gap-1.5"><label className="label-field">{t("wiz.application.label")}</label><FieldHelp k="help.application" label={t("wiz.application.label")} testid="help-application" /></div>
                <select className="field-select" value={form.application} onChange={(e) => { set("application", e.target.value); if (e.target.value !== APPLICATION_OTHER) set("application_other_text", ""); }} data-testid="select-application">
                  <option value="">{t("wiz.application.placeholder")}</option>
                  {(APPLICATIONS[form.sector] || []).map((a) => <option key={a.value} value={a.value}>{t(a.key)}</option>)}
                </select>
                {form.application === APPLICATION_OTHER && (
                  <div className="mt-4">
                    <label className="label-field" htmlFor="application-other">{t("wiz.app_other.label")}</label>
                    <input id="application-other" className="field-input" type="text" required maxLength={80}
                      value={form.application_other_text || ""} onChange={(e) => set("application_other_text", e.target.value)}
                      placeholder={t("wiz.app_other.placeholder")} data-testid="input-application-other" />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <StepHeading title={t("wiz.step.2")} sub={t("wiz.location.sub")} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-1.5"><label className="label-field">{t("wiz.country.label")}</label><FieldHelp k="help.country" label={t("wiz.country.label")} testid="help-country" /></div>
                  <select className="field-select" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="select-country">
                    {countryList.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                  </select>
                  <p className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                    {t("wiz.country.region")} <span className="font-mono">{countries[form.country]?.region ? t("region." + countries[form.country].region, countries[form.country].region) : "—"}</span> ·
                    {" "}{t("wiz.country.cost_mult")} <span className="font-mono">{num(countries[form.country]?.region_mult ?? 1, 2, 2)}</span>
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-1.5"><label className="label-field">{t("wiz.project_name.label")}</label><FieldHelp k="help.project_name" label={t("wiz.project_name.label")} testid="help-project_name" /></div>
                  <input className="field-input" type="text" placeholder={t("wiz.project_name.placeholder")} value={form.project_name} onChange={(e) => set("project_name", e.target.value)} data-testid="input-project-name" />
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <StepHeading title={t("wiz.step.3")} sub={t("wiz.load.sub")} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <NumberField label={t("wiz.peak.label")} help="help.peak_kw" helpTestid="help-peak_kw" value={form.peak_kw} onChange={(v) => set("peak_kw", v)} testid="input-peak-kw" />
                  {/* iter45ae — Peak Reconciliation Chip. Display-only: shows
                       the buyer that firm capacity target = peak × 1.20 the
                       moment they type peak_kw, so the reserve uplift is
                       explicit before they hit Generate. */}
                  {form.peak_kw > 0 && (
                    <div
                      className="mt-2 text-xs font-mono"
                      style={{ color: "var(--muted)", letterSpacing: "0.02em" }}
                      data-testid="peak-reconciliation-chip"
                    >
                      {t("wiz.peak_chip.peak")} <span style={{ color: "var(--charcoal)", fontWeight: 600 }}>{unit(form.peak_kw, "kW", 3)}</span>
                      {" "}{t("wiz.peak_chip.target")}{" "}
                      <span style={{ color: "var(--brand-green)", fontWeight: 600 }} data-testid="peak-reconciliation-firm-target">
                        {unit(Math.round(form.peak_kw * 1.20), "kW", 0)}
                      </span>
                    </div>
                  )}
                </div>
                <NumberField label={t("wiz.day_kwh.label")} help="help.day_kwh" helpTestid="help-day_kwh" value={form.day_kwh} onChange={(v) => set("day_kwh", v)} testid="input-day-kwh" />
                <div>
                  <div className="flex items-center gap-1.5"><label className="label-field">{t("wiz.lf.title")}</label><FieldHelp k="help.lf_presets" label={t("wiz.lf.title")} testid="help-lf_presets" /></div>
                  <div className="text-xs mb-2" style={{ color: "var(--muted)" }} data-testid="loadfactor-preset-hint">
                    {t("wiz.lf.hint")}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {LOAD_PATTERNS.map((lp) => (
                      <button key={lp.id} onClick={() => set("load_factor", lp.lf)} className={`p-3 rounded-md border text-sm text-left transition-colors ${form.load_factor === lp.lf ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.05)]" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`} data-testid={`loadpattern-${lp.id}`}>
                        <div className="font-medium font-mono" style={{ color: "var(--charcoal)" }}>{t(lp.labelKey)}</div>
                        <div className="mt-1" style={{ fontSize: "0.68rem", lineHeight: 1.35, color: "var(--muted)" }}>{t(lp.subKey)}</div>
                      </button>
                    ))}
                  </div>
                </div>
                {lfDerived == null ? (
                  <NumberField label={t("wiz.lf.label")} help="help.load_factor" helpTestid="help-load_factor" value={form.load_factor} step={0.05} min={0.1} max={0.95} onChange={(v) => set("load_factor", v)} testid="input-load-factor" />
                ) : (
                  <div>
                    <div className="flex items-center gap-1.5"><label className="label-field">{t("wiz.lf.derived_label")}</label><FieldHelp k="help.load_factor" label={t("wiz.lf.derived_label")} testid="help-load_factor" /></div>
                    <input type="text" readOnly className="field-input font-mono" value={loadErr ? "—" : num(lfDerived, 3, 3)} data-testid="input-load-factor" />
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }} data-testid="load-factor-derived-note">{t("wiz.load.derived")}</div>
                    {loadErr && <div className="text-xs mt-1" style={{ color: "#B42318" }} data-testid="load-input-error">{t(loadErr)}</div>}
                    {!loadErr && lfDerived < 0.15 && <div className="text-xs mt-1" style={{ color: "#B54708" }} data-testid="load-peaky-warning">{t("wiz.load.warn_peaky")}</div>}
                  </div>
                )}
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <StepHeading title={t("wiz.step.4")} sub={t("wiz.site.sub").replace("{{country}}", countryName(form.country))} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <NumberField label={t("wiz.altitude.label")} help="help.altitude" helpTestid="help-altitude" value={form.altitude_m} step={50} min={0} onChange={(v) => set("altitude_m", v)} testid="input-altitude" />
                <NumberField label={t("wiz.ambient.label")} help="help.ambient" helpTestid="help-ambient" value={form.ambient_c} step={1} min={-10} max={60} onChange={(v) => set("ambient_c", v)} testid="input-ambient" />
                <NumberField label={t("wiz.irradiance.label")} help="help.irradiance" helpTestid="help-irradiance" value={form.irradiance} step={0.1} min={2} max={8} onChange={(v) => set("irradiance", v)} testid="input-irradiance" />
              </div>
              <div className="indicative-banner mt-6 text-xs">
                {t("wiz.site.derate_banner")}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <StepHeading title={t("wiz.step.5")} sub={t("wiz.grid.sub")} help="help.grid" helpTestid="help-grid"
                helpItems={GRIDS.map((g) => ({ id: g.id, term: g.key, text: `help.grid.${g.id}.d` }))} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {GRIDS.map((g) => (
                  <SelectTile key={g.id} label={t(g.key)} selected={form.grid_status === g.id} onClick={() => set("grid_status", g.id)} testid={`grid-${g.id}`} />
                ))}
              </div>
              {form.grid_status === "weak_grid" && (
                <div className="mt-6 max-w-sm">
                  <NumberField label={t("wiz.outage.label")} help="help.outage_hours" helpTestid="help-outage_hours" value={form.outage_hours} step={1} min={0} max={20} onChange={(v) => set("outage_hours", v)} testid="input-outage-hours" />
                </div>
              )}
              <div className="mt-6">
                <NumberField label={t("wiz.fuel_price.label").replace("{{country}}", countryName(form.country))} help="help.fuel_price" helpTestid="help-fuel_price" value={form.fuel_price} step={0.05} min={0.3} max={3} onChange={(v) => set("fuel_price", v)} testid="input-fuel-price" />
              </div>
            </>
          )}

          {step === 5 && (
            <>
              <StepHeading title={t("wiz.step.6")} sub={t("wiz.ownership.sub")} help="help.ownership" helpTestid="help-ownership"
                helpItems={OWNERSHIPS.map((o) => ({ id: o.id, term: o.key, text: `help.own.${o.id}.d` }))} />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {OWNERSHIPS.map((o) => (
                  <SelectTile key={o.id} label={t(o.key)} selected={form.ownership === o.id} onClick={() => set("ownership", o.id)} testid={`ownership-${o.id}`} />
                ))}
              </div>
            </>
          )}

          {step === 6 && (
            <>
              <StepHeading title={t("wiz.priority.title")} sub={t("wiz.priority.sub")} help="help.priority" helpTestid="help-priority"
                helpItems={[["lowest_upfront", "lowest_upfront"], ["lowest_lifetime", "lowest_lifetime"], ["max_reliability", "max_reliability"], ["lowest_emission", "lowest_emissions"]].map(([w, h]) => ({ id: w, term: `wiz.priority.${w}`, text: `help.prio.${h}` }))} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PRIORITIES.map((p) => (
                  <SelectTile key={p.id} label={t(p.labelKey)} selected={form.priority === p.id} onClick={() => set("priority", p.id)} testid={`priority-${p.id}`} />
                ))}
              </div>
              {/* Advanced thermal / gas add-on (iter43) — auto-hidden for sites < 1 MW peak. */}
              <ThermalOptions peakKw={form.peak_kw} onChange={setThermalSelection} />
              {/* iter45ao Prompt-H Step 2 — First-class gas availability.
                   Buyer picks their fuel supply situation. When left as
                   `unknown`, the results page + PDF render an amber
                   "confirm this before advancing" banner. */}
              <div className="mt-6" data-testid="gas-availability-block">
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="text-sm font-medium block" style={{ color: "var(--ink)" }}>
                    {t("wiz.gas_avail.title")}
                  </label>
                  <FieldHelp k="help.gas_avail" label={t("wiz.gas_avail.title")} testid="help-gas_avail" />
                </div>
                <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
                  {t("wiz.gas_avail.hint")}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { id: "pipeline",                 labelKey: "wiz.gas_avail.pipeline" },
                    { id: "virtual_pipeline_lng_cng", labelKey: "wiz.gas_avail.vp" },
                    { id: "none",                     labelKey: "wiz.gas_avail.none" },
                    { id: "unknown",                  labelKey: "wiz.gas_avail.unknown" },
                  ].map((opt) => (
                    <SelectTile
                      key={opt.id}
                      label={t(opt.labelKey)}
                      selected={(form.gas_availability || "unknown") === opt.id}
                      onClick={() => set("gas_availability", opt.id)}
                      testid={`gas-availability-${opt.id}`}
                    />
                  ))}
                </div>
              </div>
              {/* J3 (Prompt J) — First-class diesel availability. Sits
                   directly beside gas_availability so the buyer picks
                   BOTH fuel axes on one screen. Selecting "none" is what
                   makes the physically all-red state reachable without
                   monkeypatching. */}
              <div className="mt-6" data-testid="diesel-availability-block">
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="text-sm font-medium block" style={{ color: "var(--ink)" }}>
                    {t("wiz.diesel_avail.title")}
                  </label>
                  <FieldHelp k="wiz.diesel_avail.hint" label={t("wiz.diesel_avail.title")} testid="help-diesel_avail" />
                </div>
                <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>
                  {t("wiz.diesel_avail.hint")}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { id: "available",   labelKey: "wiz.diesel_avail.available" },
                    { id: "constrained", labelKey: "wiz.diesel_avail.constrained" },
                    { id: "none",        labelKey: "wiz.diesel_avail.none" },
                  ].map((opt) => (
                    <SelectTile
                      key={opt.id}
                      label={t(opt.labelKey)}
                      selected={(form.diesel_availability || "available") === opt.id}
                      onClick={() => set("diesel_availability", opt.id)}
                      testid={`diesel-availability-${opt.id}`}
                    />
                  ))}
                </div>
              </div>
              <div className="indicative-banner mt-8">
                {t("wiz.priority.banner")}
              </div>
            </>
          )}

          {/* Nav */}
          <div className="mt-10 flex items-center justify-between pt-6" style={{ borderTop: "1px solid var(--hairline)" }}>
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="btn-secondary disabled:opacity-40" data-testid="btn-back">
              <ArrowLeft size={14} /> {t("wiz.back")}
            </button>
            {step < STEPS_KEYS.length - 1 ? (
              <button onClick={() => canNext() ? setStep(step + 1) : toast.error(t("wiz.toast.incomplete"))} disabled={!canNext()} className="btn-primary" data-testid="btn-next">
                {t("wiz.next")} <ArrowRight size={14} />
              </button>
            ) : (
              <button onClick={submit} disabled={loading || !canNext()} className="btn-primary" data-testid="btn-generate">
                {loading ? <><Loader2 size={16} className="animate-spin" /> {t("wiz.running")}</> : <>{t("wiz.generate")} <ArrowRight size={14} /></>}
              </button>
            )}
          </div>
        </div>

        <p className="text-xs mt-6" style={{ color: "var(--muted)" }}>
          {t("wiz.disclaimer")}
        </p>
      </div>
    </div>
  );
};

const StepHeading = ({ title, sub, help, helpItems, helpTestid }) => (
  <>
    <h2 className="font-display text-2xl md:text-3xl mb-1 flex items-center gap-2">
      <span>{title}</span>
      {help && <FieldHelp k={help} label={title} items={helpItems} testid={helpTestid} />}
    </h2>
    <p className="text-sm mb-8" style={{ color: "var(--muted)" }}>{sub}</p>
  </>
);

const NumberField = ({ label, value, onChange, step = 1, min, max, testid, help, helpTestid }) => (
  <div>
    <div className="flex items-center gap-1.5">
      <label className="label-field">{label}</label>
      {help && <FieldHelp k={help} label={label} testid={helpTestid} />}
    </div>
    <input type="number" className="field-input font-mono" value={value} step={step} min={min} max={max} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} data-testid={testid} />
  </div>
);

const SelectTile = ({ label, selected, onClick, testid }) => (
  <button onClick={onClick} className={`p-4 rounded-md border text-left transition-colors ${selected ? "border-[var(--brand-green)] bg-[rgba(26,111,43,0.05)]" : "border-[var(--hairline)] hover:border-[var(--brand-green)]"}`} data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="font-medium text-sm" style={{ color: "var(--charcoal)" }}>{label}</span>
      {selected && <Check size={16} style={{ color: "var(--brand-green)" }} />}
    </div>
  </button>
);
